import { MaterialityType } from '@upright/common/model/input/materiality-assessment/types';
import { SustainabilityMatterId } from '@upright/common/model/input/materiality-assessment/types-matters';
import { useCsdrMaterialityResultsBasePath } from '@upright/ui/common/upright/model/output/scores/csrd/csrd-company-double-materiality/single-matter-views-DEPRECATED/matter-level-score-based-materiality/matter-view-url-utils';
import { CsrdSingleMatterViewMaterialityTypePath } from '@upright/ui/common/upright/model/output/scores/csrd/csrd-company-double-materiality/single-matter-views-DEPRECATED/matter-level-score-based-materiality/types';
import { CsrdViewSubPage } from '@upright/ui/common/upright/model/output/scores/csrd/csrd-company-double-materiality/types';
import { useCallback } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import matterDescriptions from './matterDescriptions.json';

const V2_DMA_PATH = '/dashboard/';

export const getMatterDescription = (matterId: SustainabilityMatterId): string => {
  let description = '';
  const id = matterId.toString();

  const matterDescription = matterDescriptions.find(d => d.id === id);
  if (matterDescription) {
    description = matterDescription.description;
  }

  return description;
};

export const isOnV2DmaPath = (): boolean => {
  return location.pathname.includes(V2_DMA_PATH);
};

/**
 * Custom hook that builds the base path for DMA v2 materiality results.
 * Handles both team user and regular user paths.
 * @returns The base path string for materiality results
 */
export function useDmaV2MaterialityResultsBasePath(): string {
  const params = useParams();
  const location = useLocation();

  const enterpriseBasePath = useCsdrMaterialityResultsBasePath();

  const isInDashboard = location.pathname.includes('dashboard');

  // Team users and consultants can navigate to the dashboard of another company than their own organisational one.
  // Such dashboards are always of format companyId/companyName/dashboard.
  const shouldUseDashboardWithId = isInDashboard && 'id' in params && 'name' in params;

  if (shouldUseDashboardWithId) {
    return `/company/${params.id}/${params.name}${V2_DMA_PATH}${CsrdViewSubPage.MATERIALITY_RESULTS}`;
  }

  if (isInDashboard) {
    return `${V2_DMA_PATH}${CsrdViewSubPage.MATERIALITY_RESULTS}`;
  }

  return enterpriseBasePath;
}

/**
 * Custom hook that generates URLs for single matter views in DMA v2.
 * @returns A function that generates the URL for a given matter ID and materiality type
 */
export function useDmaV2SingleMatterViewPathGetter() {
  const materialityResultsBasePath = useDmaV2MaterialityResultsBasePath();

  return useCallback(
    (matterId: SustainabilityMatterId, materialityType: MaterialityType) => {
      return getDmaV2SingleMatterViewUrl(materialityResultsBasePath, matterId, materialityType);
    },
    [materialityResultsBasePath],
  );
}

/**
 * Generates the URL for a single matter view in DMA v2 context.
 * @param materialityResultsBasePath - The base path for materiality results
 * @param matterId - The sustainability matter ID
 * @param materialityType - The materiality type (impact or financial)
 * @returns The complete URL for the single matter view
 */
function getDmaV2SingleMatterViewUrl(
  materialityResultsBasePath: string,
  matterId: SustainabilityMatterId,
  materialityType: MaterialityType,
) {
  const materialityTypePath =
    materialityType === MaterialityType.IMPACT
      ? CsrdSingleMatterViewMaterialityTypePath.IMPACT
      : CsrdSingleMatterViewMaterialityTypePath.FINANCIAL;

  return `${materialityResultsBasePath}/${matterId}/${materialityTypePath}`;
}
